/**
 * /calculer-mes-aides — Calculateur d'aides à la rénovation.
 *
 * Page outil dédiée — fonctionne en standalone (layout: 'tool').
 *
 * Architecture :
 *  - Sous-barre outil (back + label + durée estimée)
 *  - Hero éditorial avec mesh background
 *  - Layout 2 colonnes (funnel + sidebar trust signals)
 *  - Section "Comment ça marche" en 6 étapes
 *  - FAQ rapide
 *  - Trust strip final
 */

import { useEffect } from 'react';
import {
  BadgeCheck,
  CheckCircle2,
  Clock,
  HelpCircle,
  Layers,
  Phone,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../../components/layout/Container';
import { ToolPageSubHeader } from '../../components/layout/ToolPageLayout';
import { MeshBackground } from '../../components/motion/MeshBackground';
import { Reveal } from '../../components/motion/Reveal';
import { Button } from '../../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { AidsCalculatorFunnel } from '../../components/tools/AidsCalculatorFunnel';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../../components/seo/jsonld/FAQPage';
import { cn } from '../../lib/cn';
import { useEffectiveAudience } from '../../hooks/useEffectiveAudience';

/* ─── Aides couvertes (sidebar) ─────────────────────────── */

interface AideShort {
  label: string;
  amount: string;
  tone: 'brand' | 'accent' | 'lavender' | 'sage';
}

const AIDS_SHORT: AideShort[] = [
  { label: 'MaPrimeRénov’', amount: "jusqu'à 63 000 €", tone: 'brand' },
  { label: 'Primes CEE', amount: "jusqu'à 12 000 €", tone: 'accent' },
  { label: 'Éco-PTZ', amount: "jusqu'à 50 000 €", tone: 'lavender' },
  { label: 'TVA 5,5 %', amount: 'au lieu de 20 %', tone: 'sage' },
];

const TONE_STYLES = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  lavender: 'bg-lavender-50 text-lavender-700 ring-lavender-200/60',
  sage: 'bg-sage-100 text-sage-700 ring-sage-200/60',
} as const;

/* ─── Trust signals ─────────────────────────────────────── */

interface TrustSignal {
  icon: LucideIcon;
  label: string;
  detail: string;
}

const TRUST_SIGNALS: TrustSignal[] = [
  {
    icon: ShieldCheck,
    label: 'Barèmes officiels 2026',
    detail: 'Plafonds Anah & MaPrimeRénov’ à jour, primes CEE Coup de pouce en vigueur.',
  },
  {
    icon: BadgeCheck,
    label: 'Mandataire CEE agréé',
    detail: 'Habilitation préfectorale pour déposer vos dossiers MPR + CEE.',
  },
  {
    icon: Sparkles,
    label: 'Sans engagement',
    detail: 'Calcul gratuit, recontact uniquement si vous le demandez.',
  },
];

/* ─── Étapes "Comment ça marche" ────────────────────────── */

const STEPS = [
  {
    num: '1',
    title: 'Votre profil',
    description: 'Propriétaire, bailleur, copro, locataire + commune.',
  },
  { num: '2', title: 'Votre logement', description: 'Maison, appartement ou copropriété.' },
  { num: '3', title: 'Vos revenus', description: 'RFR + foyer fiscal pour le taux MaPrimeRénov’.' },
  {
    num: '4',
    title: 'Vos travaux',
    description: 'Isolation, chauffage, ventilation, rénovation globale.',
  },
  { num: '5', title: 'Vos coordonnées', description: 'Nom, prénom, téléphone et adresse.' },
  {
    num: '6',
    title: 'Vos aides',
    description: 'Estimation détaillée + recontact sous 24 h ouvrées.',
  },
];

/* ─── FAQ rapide ────────────────────────────────────────── */

const FAQ_ITEMS = [
  {
    question: 'MaPrimeRénov’ est-elle cumulable avec les primes CEE ?',
    answer:
      "Oui — c'est même le cœur d'un dossier bien monté. MPR et CEE sont cumulables sur l'essentiel des travaux (isolation, PAC, chaudière biomasse, ventilation, ECS solaire…). Notre calculateur affiche directement le cumul possible selon votre profil et votre département.",
  },
  {
    question: "C'est quoi le Parcours accompagné (ex-Rénovation d'ampleur) ?",
    answer:
      "Le Parcours accompagné MaPrimeRénov’ (ex-Rénovation d'ampleur) finance les bouquets de travaux qui visent un gain énergétique significatif (au moins 35 %). Plafond jusqu'à 63 000 € pour les ménages très modestes. Il nécessite un audit énergétique et l'accompagnement par un AMO Rénov agréé.",
  },
  {
    question: 'Sur quels barèmes le calcul est-il basé ?',
    answer:
      'Barèmes officiels MaPrimeRénov’ 2026 (Anah), plafonds par tranche de revenus, zones Anah (A bis / A / B1 / B2 / C), bonifications Coup de Pouce CEE en vigueur, plafonds éco-PTZ. Les chiffres restent indicatifs — un devis RGE est nécessaire pour engager les dossiers.',
  },
  {
    question: 'Mes données sont-elles partagées immédiatement ?',
    answer:
      "Non — le calcul des aides se fait côté navigateur, sans envoi serveur, jusqu'à l'étape « coordonnées ». À ce moment seulement, les données sont transmises à Solution Éco France pour vous adresser votre estimation détaillée et préparer votre dossier (RGPD art. 6.1.b).",
  },
  {
    question: 'Suis-je obligé(e) de me faire recontacter par un conseiller ?',
    answer:
      "Non. L'estimation s'affiche immédiatement à l'écran. Un conseiller vous rappelle uniquement si vous nous transmettez votre numéro et acceptez l'envoi. Vous gardez la main à chaque étape.",
  },
];

export default function CalculerMesAidesPage() {
  const { effective } = useEffectiveAudience();

  useEffect(() => {
    if (effective?.id === 'particuliers') {
      window.location.replace('/particuliers/calculer-mes-aides');
    } else if (effective?.id === 'coproprietes') {
      window.location.replace('/coproprietes/calculer-mes-aides');
    }
  }, [effective?.id]);

  return (
    <div className="relative bg-paper">
      {/* ── SEO ── */}
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          {
            name: 'Calculer mes aides',
            url: 'https://solutionecofrance.fr/calculer-mes-aides',
          },
        ]}
      />
      <FAQPageLd items={FAQ_ITEMS} />

      {/* ── Sous-barre outil ── */}
      <ToolPageSubHeader
        toolLabel="Calculateur d'aides à la rénovation"
        backHref="/outils"
        backLabel="Tous les outils"
        rightSlot={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600">
            <Clock className="h-3 w-3" strokeWidth={2} />
            ~2 min
          </span>
        }
      />

      {/* ── Hero éditorial ── */}
      <section className="relative overflow-hidden border-b border-stone-200/60">
        <MeshBackground variant="warm" intensity="soft" withGrid />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(16,185,129,0.08),transparent)]"
        />
        <Container className="relative pb-10 pt-12 md:pb-14 md:pt-16">
          <div className="mx-auto max-w-4xl text-center">
            <Reveal>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200/60 bg-brand-50/60 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-700 backdrop-blur-sm">
                <Sparkles className="h-3 w-3 text-accent-500" strokeWidth={2.4} />
                Calculateur officiel 2026 · sans inscription
              </span>
              <h1 className="h-hero mt-5">
                Toutes vos aides énergétiques,{' '}
                <span className="bg-gradient-to-br from-brand-700 via-brand-600 to-accent-600 bg-clip-text text-transparent">
                  calculées en 2 minutes
                </span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-stone-600 md:text-[16px]">
                MaPrimeRénov’, primes CEE, éco-PTZ et TVA 5,5 % — un seul parcours pour estimer le
                montant cumulable et préparer votre dossier. Données restant côté navigateur jusqu'à
                votre accord explicite.
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <ul className="mx-auto mt-6 flex max-w-3xl flex-wrap justify-center gap-2">
                {[
                  'Barèmes officiels 2026',
                  'Cumul MPR + CEE + Éco-PTZ',
                  'Sans inscription',
                  'Estimation immédiate',
                ].map((benefit) => (
                  <li
                    key={benefit}
                    className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white/90 px-3 py-1.5 text-[12px] font-medium text-stone-700 shadow-[0_1px_2px_rgba(10,10,10,.04)] backdrop-blur-sm"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" strokeWidth={2.4} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* ── Layout principal : Funnel + Sidebar ── */}
      <section className="py-8 md:py-12">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10 xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-12">
            {/* ── Colonne principale — funnel ── */}
            <div className="min-w-0">
              <AidsCalculatorFunnel />
            </div>

            {/* ── Sidebar — aides + garanties + contact ── */}
            <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
              {/* Aides couvertes */}
              <div className="rounded-2xl border border-stone-200/70 bg-white p-5 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]">
                <div className="flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5 text-stone-400" strokeWidth={1.8} />
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                    Aides couvertes
                  </p>
                </div>
                <ul className="mt-3 space-y-2">
                  {AIDS_SHORT.map((aid) => (
                    <li
                      key={aid.label}
                      className="flex items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 ring-1 ring-inset ring-stone-100"
                    >
                      <span className="text-[13px] font-medium text-ink">{aid.label}</span>
                      <span
                        className={cn(
                          'rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
                          TONE_STYLES[aid.tone],
                        )}
                      >
                        {aid.amount}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-[11px] leading-relaxed text-stone-500">
                  Cumulables sous conditions — règles 2026 intégrées au calcul.
                </p>
              </div>

              {/* Garanties */}
              <div className="rounded-2xl border border-stone-200/70 bg-white p-5 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                  Nos garanties
                </p>
                <ul className="mt-3 space-y-3">
                  {TRUST_SIGNALS.map((t) => {
                    const Icon = t.icon;
                    return (
                      <li key={t.label} className="flex items-start gap-2.5">
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/50">
                          <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-ink">{t.label}</p>
                          <p className="mt-0.5 text-[12px] leading-relaxed text-stone-500">
                            {t.detail}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Contact expert */}
              <div className="overflow-hidden rounded-2xl border border-stone-200/70 bg-gradient-to-br from-ink via-stone-900 to-ink p-5 text-ivory-50 shadow-[0_8px_24px_-8px_rgba(10,10,10,.30)]">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm">
                    <HelpCircle className="h-4 w-4 text-accent-300" strokeWidth={1.8} />
                  </span>
                  <p className="text-[13px] font-semibold">Besoin d'aide ?</p>
                </div>
                <p className="mt-2.5 text-[12.5px] leading-relaxed text-stone-300">
                  Un conseiller vous accompagne dans le choix de vos travaux et le montage du
                  dossier. Réponse sous 24 h ouvrées.
                </p>
                <a
                  href="/contact?source=calculer-mes-aides"
                  className="mt-3.5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-[12.5px] font-semibold text-ivory-50 backdrop-blur-sm transition-colors hover:bg-white/20"
                >
                  <Phone className="h-3.5 w-3.5" strokeWidth={2} />
                  Parler à un expert
                </a>
              </div>
            </aside>
          </div>
        </Container>
      </section>

      {/* ── Comment ça marche ── */}
      <section className="border-y border-stone-200/60 bg-stone-50/60 py-14 md:py-20">
        <Container>
          <Reveal>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
                En 6 étapes
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
                Comment fonctionne le calcul
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-stone-600">
                Pas de jargon. Pas d'inscription. Pas de démarchage. Juste un calcul transparent
                basé sur les barèmes officiels 2026.
              </p>
            </div>
          </Reveal>

          <div className="relative mx-auto max-w-6xl">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-[22px] hidden h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent lg:block"
            />
            <div className="grid gap-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-6">
              {STEPS.map((step) => (
                <Reveal key={step.num} delay={Number(step.num) * 0.04}>
                  <div>
                    <span className="relative flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-white font-mono text-[13px] font-bold text-ink shadow-[0_1px_3px_rgba(10,10,10,.06)]">
                      {step.num}
                    </span>
                    <h3 className="mt-4 font-display text-[15px] font-semibold tracking-tight text-ink">
                      {step.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
                      {step.description}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section className="py-14 md:py-20">
        <Container size="narrow">
          <Reveal>
            <div className="mb-8">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
                FAQ
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
                Questions fréquentes
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <Accordion
              type="single"
              collapsible
              className="rounded-2xl border border-stone-200 bg-white px-6 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]"
            >
              {FAQ_ITEMS.map((item, idx) => (
                <AccordionItem key={item.question} value={`q-${idx}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-stone-600">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild variant="brand" size="lg" className="gap-2">
                <a href="#aids-funnel-top">
                  Démarrer le calcul
                  <Sparkles className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild variant="secondary" size="lg">
                <a href="/contact?source=calculer-mes-aides">Parler à un conseiller</a>
              </Button>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ── Trust strip final ── */}
      <div className="border-t border-stone-200/70 bg-stone-50/70 py-8">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[12.5px] text-stone-500">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <ShieldCheck className="h-3.5 w-3.5 text-brand-500" strokeWidth={2.2} />
              Barèmes officiels France Rénov' 2026
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="h-3.5 w-3.5 text-brand-500" strokeWidth={2.2} />
              Réseau d'artisans RGE certifiés
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <BadgeCheck className="h-3.5 w-3.5 text-brand-500" strokeWidth={2.2} />
              Mandataire CEE habilité préfecture
            </span>
          </div>
        </Container>
      </div>
    </div>
  );
}
